import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as api from "../../api/index";
import { IoMdArrowBack } from "react-icons/io";
import { FiShield, FiRefreshCw, FiChevronRight, FiInfo } from "react-icons/fi";

export default function OTP({ refreshAuth }) {
  const hasSentOtp = useRef(false);

  const [otp, setOtp] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [receivedOtp, setReceivedOtp] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const location = useLocation();
  const email = location.state?.email || "";
  const navigate = useNavigate();

  const handleChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 3) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    }
  };

  const resendOtp = async () => {
    if (!email) {
      navigate(-1);
      return;
    }
    setResendLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await api.SendOTP(email);
      if (res.data.success) {
        setSuccess("A new code has been generated.");
        if (res.data.otp) {
          setReceivedOtp(res.data.otp);
        }
      } else {
        setError(res.data.message || "Failed to send OTP.");
      }
    } catch (err) {
      setError("Unable to resend OTP. Please try again later.");
    } finally {
      setResendLoading(false);
    }
  };

  const verifyOtp = async () => {
    const code = otp.join("");
    if (code.length !== 4) {
      setError("Please enter the 4-digit security code.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const res = await api.VerifyOTP({ email, otp: code });
      if (res.data.success) {
        setSuccess("✅ Access Granted!");
        if (res.data?.token) {
          localStorage.setItem("token", res.data?.token);
          if (refreshAuth) refreshAuth();
        }
        setTimeout(() => navigate("/"), 1000);
      } else {
        setError(res.data.message || "Invalid code. Check and try again.");
        setOtp(["", "", "", ""]);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Verification failed.");
      setOtp(["", "", "", ""]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasSentOtp.current) {
      resendOtp();
      hasSentOtp.current = true;
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-orange-100 rounded-full blur-3xl opacity-50"></div>
      <div className="absolute bottom-[-10%] left-[-10%] w-96 h-96 bg-blue-50 rounded-full blur-3xl opacity-50"></div>

      {/* Header */}
      <div className="p-6 relative z-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-900 group"
        >
          <div className="p-2 bg-white rounded-full shadow-sm group-hover:bg-orange-50 transition-colors">
            <IoMdArrowBack size={20} />
          </div>
          <span className="text-xs font-black uppercase tracking-widest">Secure Exit</span>
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6 relative z-10">
        
        {/* Security Password Recovery Note */}
        <div className="max-w-md w-full mb-6 bg-blue-50/50 border border-blue-100 rounded-2xl p-4 flex items-start gap-3">
          <FiInfo className="text-blue-500 mt-0.5 shrink-0" size={16} />
          <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider leading-relaxed">
            <span className="block mb-1">Security Protocol:</span>
            If you forget your password, you will be required to receive a recovery code via your registered email: <span className="text-blue-900 underline">{email}</span>.
          </p>
        </div>

        <div className="max-w-md w-full bg-white rounded-[3rem] p-10 shadow-2xl shadow-gray-200/50 border border-gray-50 text-center">
          
          <div className="mb-10">
            <div className="w-20 h-20 bg-orange-50 text-orange-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-inner">
              <FiShield size={40} />
            </div>
            <h1 className="text-3xl font-black tracking-tighter text-gray-900 mb-3">
              Check Your Mail
            </h1>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-relaxed">
              Enter the 4-digit security code <br /> sent to <span className="text-gray-900">{email}</span>
            </p>
          </div>

          {receivedOtp && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-2xl animate-in zoom-in-95 duration-300">
              <p className="text-[10px] font-black uppercase tracking-widest text-orange-600 mb-1">
                Your Code is
              </p>
              <div className="text-2xl font-black text-orange-700 tracking-[0.5em]">
                {receivedOtp}
              </div>
            </div>
          )}

          {/* OTP Inputs */}
          <div className="flex justify-center gap-4 mb-10">
            {otp.map((digit, i) => (
              <input
                key={i}
                id={`otp-${i}`}
                type="text"
                inputMode="numeric"
                maxLength="1"
                value={digit}
                onKeyDown={(e) => handleKeyDown(e, i)}
                onChange={(e) => handleChange(e.target.value, i)}
                className="w-16 h-20 text-center text-2xl font-black bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 focus:ring-0 transition-all shadow-sm"
                disabled={loading}
              />
            ))}
          </div>

          {/* Verification Logic */}
          <div className="space-y-6">
            <button
              onClick={verifyOtp}
              disabled={loading}
              className="w-full py-5 bg-gray-900 text-white rounded-[1.5rem] font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-gray-200 hover:bg-black transition-all active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  Verify Account
                  <FiChevronRight size={18} />
                </>
              )}
            </button>

            {/* Resend Trigger */}
            <div className="flex flex-col items-center gap-4">
              <button
                onClick={resendOtp}
                disabled={resendLoading}
                className="group flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-orange-600 transition-colors"
              >
                <FiRefreshCw className={`${resendLoading ? 'animate-spin' : 'group-hover:rotate-180'} transition-transform duration-500`} />
                {resendLoading ? "Requesting New Code..." : "Resend Security Code"}
              </button>
            </div>
          </div>

          {/* Feedback Messages */}
          {(error || success) && (
            <div className={`mt-8 p-4 rounded-2xl border text-[10px] font-black uppercase tracking-wider animate-in fade-in slide-in-from-bottom-2 ${
              success 
              ? "bg-green-50 border-green-100 text-green-600" 
              : "bg-red-50 border-red-100 text-red-600"
            }`}>
              {error || success}
            </div>
          )}
        </div>
      </div>

      {/* Footer Info */}
      <div className="p-8 text-center">
        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
          Secure verification powered by <br /> 
          <span className="text-gray-900">Your Platform Security</span>
        </p>
      </div>
    </div>
  );
}